/**
 * The commission board (2026-09-04).
 *
 * User: "But those kind of items, how can I use them? What purpose of those
 * items?"
 *
 * ## What this screen is for
 *
 * A curio had one verb — Sell — so the stash was a queue for a button and the
 * twenty entries in the catalogue were twenty different amounts of the same
 * number. This board is the second verb: three people at the Sconce want
 * specific things today, and they pay more than the vendor for them.
 *
 * ## Why each card leads with the surplus, not the payout
 *
 * The payout on its own is not a decision. "300 Wax for a Coin Hoard" means
 * nothing until you know the vendor pays 150 for the same thing — the number
 * that decides whether to walk this to the board or just sell it is the
 * *difference*, so that is the number in the largest type. The payout and the
 * vendor price are both there underneath, because a player checking the
 * arithmetic should be able to.
 *
 * ## Why "held" is on every card even at zero
 *
 * A board is read before a run as much as after one. "0 of 4" is the useful
 * state, not the empty one: it is the card telling you what to bring back.
 *
 * Pure string builders, same as the rest of `ui/`.
 */

import type { CommissionRow, CommissionTier } from '../net/commissions-api';
import { itemTileMarkup, itemIconSrc, esc } from './item-icon';
import { t, N_, formatNumber } from '../i18n/i18n';
import { itemLabel } from '../config/item-defs';

/** What each tier means in a word, so three cards read as a ladder rather than three unrelated asks. */
export const TIER_LABEL: Record<CommissionTier, string> = {
  COMMON: N_('Everyday'),
  SHALLOW: N_('Any floor'),
  DEEP: N_('Deep floors'),
};

/**
 * How long the board has left, in the coarsest honest unit.
 *
 * Minutes matter near the end and hours do not — "3h left" and "3h 12m left"
 * lead to the same decision, and the second one invites you to watch it.
 */
export function timeLeftLabel(expiresAtIso: string, now: Date): string {
  const ms = new Date(expiresAtIso).getTime() - now.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return t('New board any moment');
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return t('New board in {n}h', { n: hours });
  const minutes = Math.max(1, Math.round(ms / 60_000));
  return t('New board in {n} min', { n: minutes });
}

/** `2 of 4 held` — and the whole reason the card is worth reading before a run. */
export function heldLabel(row: CommissionRow): string {
  return t('{held} of {count} held', { held: Math.min(row.held, row.count), count: row.count });
}

export function canDeliver(row: CommissionRow): boolean {
  return !row.delivered && row.held >= row.count;
}

/**
 * The progress bar.
 *
 * Green only when it is actually full. A bar that greens on the way up reads
 * as "going well" when the card is still not actionable, and the one thing
 * this card has to communicate is whether you can press the button.
 */
function progressMarkup(row: CommissionRow): string {
  const pct = row.count > 0 ? Math.min(100, (row.held / row.count) * 100) : 0;
  const full = row.held >= row.count;
  return `
    <div class="meter h-1.5">
      <div class="meter-fill absolute inset-y-0 left-0 ${full ? 'bg-success' : 'bg-gold/60'}" style="width:${pct}%"></div>
    </div>`;
}

/**
 * The control, pinned to the bottom of the card by its wrapper.
 *
 * The payout is written out rather than passed through `amountMarkup`: that
 * helper colours the numeral gold, which is right on every card in the game
 * except one sitting on a gold plate. The first pass shipped "Deliver 84"
 * with the 84 nearly invisible.
 */
function actionMarkup(row: CommissionRow): string {
  if (row.delivered) {
    return `<span class="btn btn-stone-on w-full px-2 text-[12px] pointer-events-none">${esc(t('Delivered'))}</span>`;
  }
  if (canDeliver(row)) {
    return `<button data-deliver-commission="${esc(row.defId)}" class="btn btn-gold w-full px-2 text-[13px]">`
      + `${esc(t('Deliver for {n} Wax', { n: row.payoutWax }))}</button>`;
  }
  const short = row.count - row.held;
  return '<button class="btn btn-stone w-full px-2 text-[12px]" disabled>'
    + `${esc(t('Bring {n} more', { n: short }))}</button>`;
}

/**
 * One commission.
 *
 * The item picture opens the item's own detail sheet — the same sheet the
 * Shop and the Stash open — so "what is a Gold Ore Cluster and where do I
 * find one" is answered from the card that is asking for it, rather than
 * being a thing you go and look up.
 */
export function commissionCardMarkup(row: CommissionRow): string {
  const dim = row.delivered ? ' opacity-60' : '';
  return `
    <div class="frame-inset flex flex-col gap-2 p-3 h-full${dim}">
      <div class="flex items-start gap-3">
        <span class="block w-14 shrink-0">${itemTileMarkup({
          src: itemIconSrc(row.itemDefId),
          alt: itemLabel(row.itemDefId),
          rarity: row.tier === 'COMMON' ? 'COMMON' : 'RARE',
          badge: `x${row.count}`,
          detailAttr: `data-detail-curio="${esc(row.itemDefId)}"`,
        })}</span>
        <div class="min-w-0 flex-1">
          <!--
            The tier rides beside the patron rather than inside the eyebrow
            text. As "The Warden's Clerk · Any floor" it wrapped to three lines
            on a three-column grid and pushed that card's controls out of line
            with its neighbours'; as a corner chip over the whole card it stole
            the width the title needed and wrapped that instead. Beside the
            patron it costs the title nothing — and when a translated patron
            is too long to share the line (i18n, 2026-10-16), the chip wraps
            under it instead of squeezing it.
          -->
          <div class="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
            <p class="eyebrow text-text-dim min-w-0 max-w-full break-words hyphens-auto">${esc(t(row.patron))}</p>
            <span class="chip shrink-0 text-[10px] leading-none py-1">${esc(t(TIER_LABEL[row.tier]))}</span>
          </div>
          <p class="text-[14px] leading-tight mt-0.5 break-words hyphens-auto">${esc(t(row.title))}</p>
        </div>
      </div>
      <p class="text-[11px] text-text-dim leading-snug">${esc(t(row.flavour))}</p>

      <div class="flex items-baseline justify-between gap-2">
        <span class="text-[12px] text-text-dim">${esc(heldLabel(row))}</span>
        <span class="text-[11px] text-text-dim tabular-nums">${esc(t('{n}kg to carry', { n: formatNumber(row.askWeightKg) }))}</span>
      </div>
      ${progressMarkup(row)}

      <!--
        The auto top margin is what keeps three cards' buttons on one line whatever the
        patron's name and the flavour do to the height above them.
      -->
      <div class="mt-auto pt-1">
        <div class="flex items-baseline justify-between gap-2 mb-1.5">
          <span class="font-heading text-gold text-[15px] tabular-nums">+${row.surplusWax}</span>
          <span class="text-[11px] text-text-dim tabular-nums">${esc(t('{payout} Wax · vendor pays {vendor}', { payout: row.payoutWax, vendor: row.vendorWax }))}</span>
        </div>
        ${actionMarkup(row)}
      </div>
    </div>`;
}

/** The whole board. The panel shell, title and close button are the hub's. */
export function commissionBoardMarkup(
  board: { expiresAt: string; commissions: CommissionRow[] } | null,
  now: Date,
): string {
  if (!board || board.commissions.length === 0) {
    return `<p class="text-[13px] text-text-dim">${esc(t('The board is empty today. Come back tomorrow.'))}</p>`;
  }
  const cards = board.commissions.map(commissionCardMarkup).join('');
  return `
    <p class="text-[12px] text-text-dim mb-3">${esc(timeLeftLabel(board.expiresAt, now))}</p>
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">${cards}</div>`;
}

/** The tile subtitle — how many are ready to hand in right now, which is the only number worth a glance. */
export function boardTileSub(board: { commissions: CommissionRow[] } | null): string {
  if (!board) return t('Three asks a day');
  const ready = board.commissions.filter(canDeliver).length;
  if (ready > 0) return t('{n} ready to deliver', { n: ready });
  const left = board.commissions.filter((c) => !c.delivered).length;
  return left === 0 ? t('All delivered today') : t('{n} open', { n: left });
}
